import React, { useState, useRef, useEffect } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import api from '../api/api1';
import { useToast } from '../context/ToastContext';
import { getImageUrlWithTimestamp } from '../utils/imageUtils';

export default function ProfilePhotoUpload({ 
  currentPhoto, 
  onPhotoUpdate, 
  onProfileRefresh,
  onPhotoFileReady,
  isEditing,
  userName,
  userRole,
  size = 120
}) {
  const { showToast } = useToast();
  const [showCropModal, setShowCropModal] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => { setImageFailed(false); }, [currentPhoto]);

  const [crop, setCrop] = useState({
    unit: '%',
    width: 80, // Slightly smaller to show more of the image
    height: 80,
    x: 10, // Will be recalculated when image loads
    y: 10, // Will be recalculated when image loads
    // aspect: undefined // Remove forced aspect ratio
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageSrc = /^(blob:|data:|https?:)/i.test(currentPhoto || '')
    ? currentPhoto
    : getImageUrlWithTimestamp(currentPhoto);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log('📁 File selected:', file.name);
      
      // Store the original file for "Use Original Photo" option
      setOriginalFile(file);
      
      // Validate file type - explicitly accept JPG and PNG
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        showToast('Please select a JPG or PNG image file', 'error');
        e.target.value = '';
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showToast('Image size must be less than 5MB', 'error');
        e.target.value = '';
        return;
      }
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        console.log('🖼️ Image preview created, opening crop modal');
        setImagePreview(e.target.result);
        setShowCropModal(true);
        
        // Reset crop to centered position for new image
        setCrop({
          unit: '%',
          width: 80,
          height: 80,
          x: 10, // Centered position for 80% width: (100-80)/2 = 10
          y: 10, // Centered position for 80% height: (100-80)/2 = 10
        });
        setCompletedCrop(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadOriginalImage = async () => {
    if (!originalFile) {
      console.error('❌ No original file available for upload');
      showToast('No file selected', 'error');
      return;
    }
    
    console.log('🚀 Starting original photo upload...');
    console.log('� Original file details:', {
      name: originalFile.name,
      size: `${(originalFile.size / 1024 / 1024).toFixed(2)}MB`,
      type: originalFile.type,
      lastModified: new Date(originalFile.lastModified).toISOString()
    });
    
    setIsUploading(true);
    
    try {
      console.log('� Calling uploadCroppedImage with original file...');
      await uploadCroppedImage(originalFile, originalFile.name); // Pass original file and filename
      console.log('✅ Original file upload completed');
      handleCloseCropModal();
    } catch (error) {
      console.error('❌ Original image upload error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      showToast('Failed to upload profile photo', 'error');
    } finally {
      setIsUploading(false);
      console.log('🏁 Original upload process completed');
    }
  };

  const generateCroppedImage = async () => {
    console.log('🎯 generateCroppedImage called');
    console.log('🔍 Checking conditions:', {
      completedCrop: !!completedCrop,
      imgRef: !!imgRef.current,
      canvasRef: !!canvasRef.current,
      cropData: completedCrop
    });

    if (!completedCrop || !imgRef.current || !canvasRef.current) {
      console.error('❌ Missing required elements for cropping');
      showToast('Please select an area to crop', 'error');
      return;
    }

    const image = imgRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    console.log('📊 Image details:', {
      naturalWidth: image.naturalWidth,
      naturalHeight: image.naturalHeight,
      displayWidth: image.width,
      displayHeight: image.height
    });

    // Set canvas to expected backend size (500x500)
    const outputSize = 500;
    canvas.width = outputSize;
    canvas.height = outputSize;

    console.log('🎯 Crop dimensions:', {
      cropX: completedCrop.x,
      cropY: completedCrop.y,
      cropWidth: completedCrop.width,
      cropHeight: completedCrop.height,
      canvas: `${canvas.width}x${canvas.height}`,
      imageNaturalSize: `${image.naturalWidth}x${image.naturalHeight}`
    });

    try {
      console.log('🎨 Starting canvas operations...');
      
      // Clear canvas with white background (for JPEG compatibility)
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, outputSize, outputSize);
      console.log('✅ Canvas cleared and background filled');

      // Convert crop coordinates from percentage to actual pixels
      // ReactCrop returns coordinates relative to the displayed image, not the natural image
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      
      // Convert crop coordinates to actual image pixel coordinates
      const actualCropX = completedCrop.x * scaleX;
      const actualCropY = completedCrop.y * scaleY;
      const actualCropWidth = completedCrop.width * scaleX;
      const actualCropHeight = completedCrop.height * scaleY;

      console.log('🔄 Coordinate conversion:', {
        displaySize: `${image.width}x${image.height}`,
        naturalSize: `${image.naturalWidth}x${image.naturalHeight}`,
        scaleX: scaleX.toFixed(2),
        scaleY: scaleY.toFixed(2),
        cropDisplay: {x: completedCrop.x, y: completedCrop.y, width: completedCrop.width, height: completedCrop.height},
        cropActual: {x: actualCropX.toFixed(0), y: actualCropY.toFixed(0), width: actualCropWidth.toFixed(0), height: actualCropHeight.toFixed(0)}
      });

      // Calculate scaling to fit the actual cropped area within 500x500 canvas
      const scale = Math.min(outputSize / actualCropWidth, outputSize / actualCropHeight);
      const scaledWidth = actualCropWidth * scale;
      const scaledHeight = actualCropHeight * scale;
      
      // Center the scaled crop in the 500x500 canvas
      const x = (outputSize - scaledWidth) / 2;
      const y = (outputSize - scaledHeight) / 2;

      console.log('📐 Scaling calculations:', {
        scale: scale.toFixed(2),
        scaledWidth: scaledWidth.toFixed(0),
        scaledHeight: scaledHeight.toFixed(0),
        offsetX: x.toFixed(0),
        offsetY: y.toFixed(0)
      });

      // Draw the cropped area using ACTUAL pixel coordinates, scaled and centered
      ctx.drawImage(
        image,
        actualCropX,        // Source X (actual pixels)
        actualCropY,        // Source Y (actual pixels)
        actualCropWidth,    // Source width (actual pixels)
        actualCropHeight,   // Source height (actual pixels)
        x,                  // Destination X (centered)
        y,                  // Destination Y (centered)
        scaledWidth,        // Destination width (scaled)
        scaledHeight        // Destination height (scaled)
      );
      console.log('✅ Image drawn to canvas with correct coordinates');

      // Determine output format based on original file type from state
      const originalFileState = originalFile || { type: 'image/jpeg' };
      const isPNG = originalFileState.type === 'image/png';
      const outputFormat = isPNG ? 'image/png' : 'image/jpeg';
      const quality = isPNG ? undefined : 0.98; // PNG doesn't use quality

      console.log('🎨 Output format:', {
        format: outputFormat,
        quality: quality,
        isPNG: isPNG,
        originalFileType: originalFileState.type
      });

      console.log('🔄 Converting canvas to blob...');
      // Convert to blob and upload
      canvas.toBlob(async (blob) => {
        console.log('🎯 Blob callback triggered');
        if (blob) {
          console.log('✅ Blob created successfully:', {
            size: `${(blob.size / 1024).toFixed(2)}KB`,
            type: blob.type
          });
          
          // Generate appropriate filename
          const fileExtension = isPNG ? 'png' : 'jpg';
          const fileName = `profile-photo.${fileExtension}`;
          
          console.log('📝 Generated filename:', fileName);
          console.log('🚀 Calling uploadCroppedImage...');
          await uploadCroppedImage(blob, fileName);
        } else {
          console.error('❌ Failed to create blob from canvas');
          showToast('Failed to process cropped image', 'error');
        }
      }, outputFormat, quality);
      
    } catch (error) {
      console.error('❌ Canvas processing error:', error);
      showToast('Failed to process image', 'error');
    }
  };

  const uploadCroppedImage = async (croppedBlob, fileName) => {
    try {
      if (onPhotoFileReady) {
        onPhotoFileReady(croppedBlob, fileName);
        showToast('Photo selected. Select Save changes to upload it.', 'success');
        handleCloseCropModal();
        return;
      }
      setIsUploading(true);
      console.log('🚀 Starting upload process...');
      console.log('📁 Blob details:', {
        size: `${(croppedBlob.size / 1024 / 1024).toFixed(2)}MB`,
        type: croppedBlob.type,
        name: fileName
      });

      const formData = new FormData();
      formData.append('file', croppedBlob, fileName);

      console.log('📤 Form data prepared, sending to API...');

      // Use different endpoint based on user role
      const normalizedRole = String(userRole || '').toLowerCase().replace(/[\s_]/g, '');
      const endpoint = normalizedRole === 'tenantowner'
        ? '/api/upload/tenant-profile-photo'
        : '/api/upload/profile-photo';

      console.log('🎯 Using endpoint:', endpoint);

      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('📥 API Response:', {
        status: response.status,
        data: response.data,
        success: response.data?.success
      });
      
      if (response.data.success) {
        console.log('✅ Upload successful, image URL:', response.data.image_url);
        onPhotoUpdate(response.data.image_url);
        showToast('Profile photo uploaded successfully!', 'success');
        handleCloseCropModal();
        // Trigger profile refresh to get fresh data from server
        if (onProfileRefresh) {
          onProfileRefresh();
        }
      } else {
        console.error('❌ Upload failed:', response.data);
        showToast(response.data.error || 'Failed to upload profile photo', 'error');
      }
    } catch (error) {
      console.error('❌ Upload error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      showToast(error.response?.data?.error || 'Failed to upload profile photo', 'error');
    } finally {
      setIsUploading(false);
      console.log('🏁 Upload process completed');
    }
  };

  const handleCloseCropModal = () => {
    if (isUploading) {
      console.log('🚫 Cannot close modal while uploading');
      return;
    }
    setShowCropModal(false);
    setImagePreview(null);
    setOriginalFile(null); // Clear original file
    setCrop({
      unit: '%',
      width: 80, // Slightly smaller to show more of the image
      height: 80,
      x: 10, // Center the crop area
      y: 10,
      // aspect: undefined // Remove forced aspect ratio
    });
    setCompletedCrop(null);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    console.log('🔒 Crop modal closed - no upload performed');
  };

  return (
    <div>
      {/* Profile Photo Display */}
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <div style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: '50%',
          background: '#16877d',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: `${Math.round(size * 0.4)}px`,
          fontWeight: '700',
          overflow: 'hidden',
          position: 'relative',
          border: `${size > 100 ? 4 : 3}px solid #fff`,
          boxSizing: 'border-box',
          boxShadow: size > 100 ? '0 0 0 1px #cde4df, 0 6px 18px rgba(26,91,83,0.16)' : '0 0 0 1px #cde4df, 0 3px 10px rgba(26,91,83,0.12)'
        }}>
          {currentPhoto && !imageFailed ? (
            <img 
              src={imageSrc}
              alt="Profile" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                display: 'block'
              }}
              onError={() => setImageFailed(true)}
            />
          ) : (
            userName?.charAt(0)?.toUpperCase() || 'U'
          )}
        </div>
        
        {isEditing && (
          <label
            title="Change profile photo"
            aria-label="Change profile photo"
            className="photo-upload-badge"
            style={{
              position: 'absolute',
              bottom: size > 100 ? '4px' : '0px',
              right: size > 100 ? '4px' : '0px',
              width: `${Math.max(26, Math.round(size * 0.3))}px`,
              height: `${Math.max(26, Math.round(size * 0.3))}px`,
              borderRadius: '50%',
              background: '#16877d',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              border: '3px solid #fff',
              boxSizing: 'border-box',
              boxShadow: '0 2px 8px rgba(18,54,59,0.25)'
            }}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" width="55%" height="55%" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 8h3l1.5-2h7L17 8h3v11H4z" />
              <circle cx="12" cy="13" r="3.2" />
            </svg>
            <input
              type="file"
              accept="image/jpeg,image/png"
              onChange={handleFileSelect}
              ref={fileInputRef}
              style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
            />
          </label>
        )}
      </div>

      {/* Crop Modal */}
      {showCropModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget && !isUploading) {
            handleCloseCropModal();
          }
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}>
            <h3 style={{ 
              margin: '0 0 20px 0', 
              fontSize: '20px', 
              fontWeight: '600',
              color: '#333'
            }}>
              Crop Profile Photo
            </h3>
            
            <div style={{ marginBottom: '20px' }}>
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                onImageLoad={(e) => {
                  // Ensure crop is centered when image loads
                  const centeredCrop = {
                    unit: '%',
                    width: 80,
                    height: 80,
                    x: 10, // (100-80)/2 = 10 (centered)
                    y: 10, // (100-80)/2 = 10 (centered)
                  };
                  setCrop(centeredCrop);
                  console.log('🎯 Image loaded, crop centered:', centeredCrop);
                }}
                // aspect={1} // Remove forced aspect ratio
                circularCrop={true}
                keepSelection={true}
              >
                <img
                  ref={imgRef}
                  src={imagePreview}
                  alt="Upload preview"
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '400px',
                    display: 'block'
                  }}
                />
              </ReactCrop>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              flexWrap: 'wrap'
            }}>
              <button
                type="button"
                onClick={handleCloseCropModal}
                disabled={isUploading}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  background: 'white',
                  color: '#666',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Skip Upload
              </button>
              <button
                type="button"
                onClick={uploadOriginalImage}
                disabled={isUploading}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #16877d',
                  background: 'white',
                  color: '#16877d',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {isUploading ? (
                  'Uploading...'
                ) : (
                  'Use Original Photo'
                )}
              </button>
              <button
                type="button"
                onClick={generateCroppedImage}
                disabled={!completedCrop || isUploading}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: (!completedCrop || isUploading) ? '#c5cfd2' : '#16877d',
                  color: 'white',
                  cursor: (!completedCrop || isUploading) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                title={!completedCrop ? 'Please select an area to crop first' : isUploading ? 'Uploading...' : 'Crop and upload image'}>
                {isUploading ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid white',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Uploading...
                  </>
                ) : (
                  'Crop & Upload'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden canvas for cropping */}
      <canvas
        ref={canvasRef}
        style={{ display: 'none' }}
      />

      <style>{`
        .photo-upload-badge { transition: background 150ms ease; }
        .photo-upload-badge:hover { background: #126f67 !important; }
        .photo-upload-badge:focus-within { outline: 2px solid #16877d; outline-offset: 2px; }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .ReactCrop__crop-selection {
          border: 2px solid #16877d;
          border-radius: 50%;
        }
        
        .ReactCrop__drag-handle {
          background: #16877d;
          border: 2px solid white;
        }
      `}</style>
    </div>
  );
}